// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapport mode's page catalogue: which `?page=` value each page has
 * (`INDBERETNING_PAGE` for Indberetning), each page's readiness (from the
 * "Før indberetning" checklist), the heading of the right tile, and the rows
 * the frontend-composed preview draws for each page. The left tile's tree
 * (`rapportTree.ts`) is a layer over it: its leaves name these pages.
 *
 * The page contents below are the fallback for a ruxd from before the report
 * preview (contract gap 4): composed here from the live case and labelled
 * "Forhåndsvisning". A current ruxd compiles the pages itself, and
 * `reportPreview.ts` puts its section verdicts on these cards. The "PDF" page
 * shows the stored document itself. Pure: no React, no DOM.
 */

import type { ReportPdfVersion, Sample, SurveyPhotos, SurveySummary, SurveyType } from '../api/types';
import { formatNumber, formatQuantity, formatTonnes, RESULT_LABEL, STAGE_LABEL, TREATMENT_LABEL, type Tone } from '../kortlaegning/vocab';
import { isComplete, UNKNOWN_STATUS, versionDate, versionStatus } from '../rapport/model';
import {
  type CheckId,
  type Checklist,
  type ProjectRecord,
  danishDate,
  filled,
  reportedTypes,
} from './checklist';
import { INDBERETNING_PAGE } from './selection';

export type PageId = 'forside' | 'sagsdata' | 'ressourcer' | 'miljoe' | 'bilag' | 'nedrivningsplan' | 'indberetning' | 'pdf';

/** `?page=6`: the latest stored PDF. */
export const PDF_PAGE = 6;

export interface PageDef {
  id: PageId;
  /** The `Selection.page` value. */
  page: number;
  title: string;
  /** A page of the report document (numbered in the strip); Indberetning and PDF are not. */
  document: boolean;
  /** The checklist rows this page's content rests on. */
  checks: readonly CheckId[];
}

/** The document's order (the retired strip's). */
export const PAGE_DEFS: readonly PageDef[] = [
  { id: 'forside', page: 0, title: 'Forside', document: true, checks: ['sagsdata'] },
  { id: 'sagsdata', page: 1, title: 'Sagsdata', document: true, checks: ['sagsdata', 'screeningApproved'] },
  { id: 'ressourcer', page: 2, title: 'Ressourcekortlægning', document: true, checks: ['approved', 'screened', 'eakTons'] },
  { id: 'miljoe', page: 3, title: 'Miljøkortlægning', document: true, checks: ['samples'] },
  { id: 'bilag', page: 4, title: 'Prøver & bilag', document: true, checks: ['samples'] },
  { id: 'nedrivningsplan', page: 5, title: 'Nedrivningsplan', document: true, checks: ['approved', 'eakTons'] },
  { id: 'indberetning', page: INDBERETNING_PAGE, title: 'Indberetning', document: false, checks: [] },
  { id: 'pdf', page: PDF_PAGE, title: 'PDF', document: false, checks: [] },
];

/** The page a `?page=` value shows; an unknown value falls back to the Forside. */
export function pageDef(page: number): PageDef {
  return PAGE_DEFS.find((p) => p.page === page) ?? PAGE_DEFS[0];
}

/** The `?page=` value of a page. */
export function pageOf(id: PageId): number {
  return PAGE_DEFS.find((p) => p.id === id)?.page ?? 0;
}

// ------------------------------------------------------------ the strip --

export interface PageModel {
  id: PageId;
  page: number;
  /** The card's title: "3. Ressourcekortlægning", "Indberetning", "PDF · v3". */
  label: string;
  title: string;
  ready: boolean;
  /** Why it is not ready (checklist wording), in row order; empty when ready. */
  reasons: string[];
  /** The card's figure line. */
  figure: string;
}

export const READY_TEXT = 'Klar';
export const INCOMPLETE_TEXT = 'Ufuldstændig';

function documentModel(def: PageDef, n: number, list: Checklist): PageModel {
  const rows = list.rows.filter((r) => def.checks.includes(r.id) && r.state !== 'ok');
  const ready = rows.length === 0;
  return {
    id: def.id,
    page: def.page,
    label: `${n}. ${def.title}`,
    title: def.title,
    ready,
    reasons: rows.map((r) => `${r.label}: ${r.detail}`),
    figure: ready ? READY_TEXT : 'Mangler data',
  };
}

function pdfModel(def: PageDef, versions: readonly ReportPdfVersion[] | null): PageModel {
  const base = { id: def.id, page: def.page, title: def.title };
  if (versions === null) return { ...base, label: def.title, ready: false, reasons: [], figure: 'Indlæser…' };
  const latest = versions[0];
  if (!latest) {
    return { ...base, label: def.title, ready: false, reasons: ['Der er ikke genereret en version endnu.'], figure: 'Ingen version' };
  }
  const status = versionStatus(latest);
  const label = `${def.title} · v${latest.version}`;
  if (status === null) return { ...base, label, ready: false, reasons: [UNKNOWN_STATUS.title], figure: UNKNOWN_STATUS.text };
  const ready = isComplete(latest);
  return { ...base, label, ready, reasons: ready ? [] : [status.title], figure: ready ? READY_TEXT : status.text };
}

/** The strip's cards, in `PAGE_DEFS` order. */
export function pageModels(list: Checklist, versions: readonly ReportPdfVersion[] | null): PageModel[] {
  let n = 0;
  return PAGE_DEFS.map((def) => {
    if (def.document) return documentModel(def, ++n, list);
    if (def.id === 'indberetning') {
      return {
        id: def.id,
        page: def.page,
        label: def.title,
        title: def.title,
        ready: list.ready,
        reasons: list.missing,
        figure: list.ready ? 'Klar til afsendelse' : 'Mangler data',
      };
    }
    return pdfModel(def, versions);
  });
}

// ------------------------------------------------------ the right tile --

export interface ReportHeading {
  /** "Rapport v3". */
  title: string;
  pill: { tone: Tone; text: string; title: string } | null;
}

/**
 * The right tile's heading: the latest version and whether it is Komplet or
 * a Kladde (`versionStatus`).
 */
export function reportHeading(versions: readonly ReportPdfVersion[] | null): ReportHeading {
  if (versions === null) return { title: 'Rapport', pill: null };
  const latest = versions[0];
  if (!latest) {
    return { title: 'Rapport', pill: { tone: 'wait', text: 'Ingen version', title: 'Der er ikke genereret en version endnu.' } };
  }
  const title = `Rapport v${latest.version}`;
  const status = versionStatus(latest);
  if (status === null) return { title, pill: { tone: 'wait', text: UNKNOWN_STATUS.text, title: UNKNOWN_STATUS.title } };
  return { title, pill: { tone: status.tone, text: status.text, title: status.title } };
}

// ------------------------------------------------------- page contents --

/** The preview's caption: the PDF is built by the server and may differ (contract gap 4). */
export const PREVIEW_CAPTION = "Forhåndsvisning — PDF'en kan afvige";

export interface CoverModel {
  heading: string;
  /** "Skolevej 12, 6000 Kolding · BBR 621-123456-1"; null when neither is known. */
  place: string | null;
  preparedBy: string | null;
  /** "Version 3 · kladde · 09.08.2026", or that there is none. */
  version: string;
  legal: string;
}

function text(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? String(value) : null;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function area(value: unknown): string | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 ? `${formatNumber(n)} m²` : null;
}

/** The Forside; `fallback` is its heading when the case has neither a name nor an address. */
export function coverModel(
  project: ProjectRecord | null,
  versions: readonly ReportPdfVersion[] | null,
  fallback = 'Ressourcekortlægning',
): CoverModel {
  const address = text(project?.building_address);
  const bbr = text(project?.bbr_number);
  const place = [address, bbr ? `BBR ${bbr}` : null].filter(Boolean).join(' · ') || null;
  const latest = versions?.[0];
  const status = latest ? versionStatus(latest) : null;
  const state = status === null ? null : latest && isComplete(latest) ? 'komplet' : 'kladde';
  const version = latest
    ? [`Version ${latest.version}`, state, versionDate(latest.created_at)].filter(Boolean).join(' · ')
    : 'Ingen version genereret endnu';
  const floor = area(project?.demolished_floor_area_m2);
  return {
    heading: text(project?.name) ?? address ?? fallback,
    place,
    preparedBy: text(project?.coordinator) ?? text(project?.survey_organisation),
    version,
    legal: `Jf. BEK 496/2024 om selektiv nedrivning.${floor ? ` Nedrevet etageareal ${floor}.` : ''}`,
  };
}

export interface FieldRow {
  label: string;
  /** Null = not filled in (the preview marks it "mangler"). */
  value: string | null;
}

export function sagsdataRows(project: ProjectRecord | null): FieldRow[] {
  const p = project;
  const date = (v: unknown) => {
    const s = text(v);
    return s ? danishDate(s) : null;
  };
  return [
    { label: 'Adresse', value: text(p?.building_address) },
    { label: 'BBR-nummer', value: text(p?.bbr_number) },
    { label: 'Matrikel', value: text(p?.cadastral) },
    { label: 'Bygherre', value: text(p?.client) },
    { label: 'Kontaktperson', value: text(p?.client_contact) },
    { label: 'Miljø- og ressourcekoordinator', value: text(p?.coordinator) },
    { label: 'Kommune', value: text(p?.municipality) },
    { label: 'Nedrivning starter', value: date(p?.demolition_start) },
    { label: 'Nedrevet etageareal', value: area(p?.demolished_floor_area_m2) },
    { label: 'Opførelsesår', value: filled(p?.year_of_construction) ? String(p?.year_of_construction) : null },
    { label: 'Kortlægningsdato', value: date(p?.survey_date) },
    { label: 'Kortlagt af', value: text(p?.survey_organisation) },
  ];
}

export interface TypeRow {
  id: number;
  name: string;
  approved: boolean;
  /** Null = no EAK code. */
  eak: string | null;
  treatment: string;
  quantity: string;
  /** Null = tons unknown (bevaring needs none, so it shows "—" rather than "mangler"). */
  mass: string | null;
  massNeeded: boolean;
}

/** Ressourcekortlægning's table: every non-rejected type, in the server's order. */
export function typeRows(types: readonly SurveyType[]): TypeRow[] {
  return reportedTypes(types).map((t) => ({
    id: t.id,
    name: t.name,
    approved: t.review_status === 'approved',
    eak: t.eak_code.trim() || null,
    treatment: TREATMENT_LABEL[t.treatment],
    quantity: formatQuantity(t.quantity, t.unit),
    mass: t.mass_t === null ? null : formatTonnes(t.mass_t),
    massNeeded: t.treatment !== 'bevaring',
  }));
}

/** "8 typer · 412,6 t i alt · 3 godkendt" — the summary's total when it is loaded. */
export function resourceSummaryLine(types: readonly SurveyType[], summary: SurveySummary | null): string {
  const list = reportedTypes(types);
  const total = summary?.total_mass_t ?? list.reduce((s, t) => s + (t.mass_t ?? 0), 0);
  const approved = list.filter((t) => t.review_status === 'approved').length;
  return `${list.length} ${list.length === 1 ? 'type' : 'typer'} · ${formatTonnes(total)} i alt · ${approved} godkendt`;
}

export interface SampleRow {
  id: number;
  code: string;
  title: string;
  what: string;
  /** The linked types' names, or null. */
  types: string | null;
  stage: string;
  /** Null until answered. */
  result: string | null;
  contaminated: boolean;
}

/** Miljøkortlægning's table. */
export function sampleRows(samples: readonly Sample[], types: readonly SurveyType[]): SampleRow[] {
  const names = new Map(types.map((t) => [t.id, t.name]));
  return samples.map((s) => {
    const linked = s.type_ids.map((id) => names.get(id)).filter((n): n is string => !!n);
    return {
      id: s.id,
      code: s.code,
      title: s.title,
      what: s.what,
      types: linked.length > 0 ? linked.join(', ') : null,
      stage: STAGE_LABEL[s.stage],
      result: s.stage === 'svar' && s.result ? RESULT_LABEL[s.result] : null,
      contaminated: s.result === 'forurenet',
    };
  });
}

export interface AppendixRow {
  /** "A", "B", … */
  letter: string;
  text: string;
  /** Null = still awaiting the lab. */
  status: string | null;
}

function letter(i: number): string {
  // A…Z, then AA, AB, … (a case with more than 26 appendices is unusual, not impossible).
  let n = i;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** Prøver & bilag: one appendix per sample (its lab report), then the photo documentation. */
export function appendixRows(samples: readonly Sample[], photos: SurveyPhotos | null): AppendixRow[] {
  const rows: AppendixRow[] = samples.map((s, i) => ({
    letter: letter(i),
    text: `${s.code} · ${s.title}`,
    status: s.stage === 'svar' ? `Svar: ${s.result ? RESULT_LABEL[s.result] : 'modtaget'}` : null,
  }));
  if (photos) {
    const parts = Object.values(photos.parts).filter((p) => p.count > 0).length;
    rows.push({
      letter: letter(rows.length),
      text: `Fotodokumentation · ${parts === 1 ? '1 bygningsdel' : `${parts} bygningsdele`} med foto`,
      status: 'Fra scanningen',
    });
  }
  return rows;
}

export interface DemolitionStep {
  title: string;
  /** Type names, a pending sample marked. */
  items: string[];
}

/**
 * Nedrivningsplan's sequence, derived from the types: environmental clean-up
 * first (forurenet, or awaiting a sample), then by the waste hierarchy.
 * Recipients are not modelled anywhere yet, so the preview says so.
 */
export function demolitionSteps(types: readonly SurveyType[]): DemolitionStep[] {
  const list = reportedTypes(types);
  const named = (pick: (t: SurveyType) => boolean) => list.filter(pick).map((t) => t.name);
  const steps: DemolitionStep[] = [
    {
      title: 'Miljøsanering',
      items: list
        .filter((t) => t.environment_status === 'forurenet' || t.environment_status === 'afventer')
        .map((t) => (t.environment_status === 'afventer' ? `${t.name} (afventer prøvesvar)` : t.name)),
    },
    { title: 'Bevares i bygningen', items: named((t) => t.treatment === 'bevaring') },
    { title: 'Udtag til genbrug', items: named((t) => t.treatment === 'genbrug') },
    { title: 'Nedtages til genanvendelse', items: named((t) => t.treatment === 'genanvendelse') },
    { title: 'Anden nyttiggørelse', items: named((t) => t.treatment === 'nyttiggoerelse') },
    { title: 'Bortskaffelse', items: named((t) => t.treatment === 'bortskaffelse') },
  ];
  return steps.filter((s) => s.items.length > 0);
}

export const RECIPIENTS_NOTE = 'Modtagere af affaldet registreres ikke i ReUseX endnu — angiv dem i PDF’ens bilag.';
